/* Wake Alarm Build — theme script (ES module). Custom elements only; no globals besides window.Theme. */
const STR = (() => {
  try { return JSON.parse(document.getElementById('ThemeStrings').textContent); } catch (e) { return {}; }
})();
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const fill = (tpl, map) => Object.keys(map).reduce((s, k) => s.split(k).join(map[k]), tpl || '');

function money(cents) {
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || STR.currency || 'USD';
  try {
    return new Intl.NumberFormat(STR.locale || document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

/* ------------------------------------------------------------------ cart API */
const Cart = {
  sectionIds() {
    return [...document.querySelectorAll('cart-drawer[data-section]')].map((el) => el.dataset.section);
  },
  async request(url, body) {
    const ids = this.sectionIds();
    const payload = Object.assign({}, body);
    if (ids.length) { payload.sections = ids; payload.sections_url = window.location.pathname; }
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || STR.errorGeneric);
      err.data = data;
      throw err;
    }
    return data;
  },
  add(items) { return this.request(STR.cartAddUrl + '.js', { items }); },
  change(key, quantity) { return this.request(STR.cartChangeUrl + '.js', { id: key, quantity }); },
  update(body) { return this.request(STR.cartUpdateUrl + '.js', body); },
  async refreshCount() {
    try {
      const res = await fetch(STR.cartUrl + '.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
      document.querySelectorAll('[data-cart-toggle]').forEach((el) => {
        if (STR.cartCountLabel) el.setAttribute('aria-label', fill(STR.cartCountLabel, { __N__: cart.item_count }));
      });
      return cart;
    } catch (e) { return null; }
  },
  render(sections) {
    if (!sections) return;
    document.querySelectorAll('cart-drawer[data-section]').forEach((drawer) => drawer.renderFrom(sections[drawer.dataset.section]));
  },
  async afterChange(data, { open = false } = {}) {
    this.render(data && data.sections);
    const cart = await this.refreshCount();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    if (open) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && STR.drawer !== false) drawer.open();
      else window.location.href = STR.cartUrl;
    }
  },
};
window.Theme = { Cart, money };

/* ------------------------------------------------------------------ modal drawer (focus trap, Escape) */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class ModalDrawer extends HTMLElement {
  connectedCallback() {
    this.onKey = this.onKey.bind(this);
    this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
  }
  get panel() { return this.querySelector('.drawer__panel') || this; }
  open(opener) {
    this.opener = opener || document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    document.addEventListener('keydown', this.onKey);
    const first = this.panel.querySelector('input[type="search"]') || this.panel;
    requestAnimationFrame(() => first.focus({ preventScroll: true }));
  }
  close() {
    if (this.getAttribute('aria-hidden') === 'true') return;
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.onKey);
    if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
  }
  onKey(e) {
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key !== 'Tab') return;
    const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}
customElements.define('modal-drawer', ModalDrawer);

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (opener) {
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && drawer.open) { e.preventDefault(); drawer.open(opener); }
    return;
  }
  const cartToggle = e.target.closest('[data-cart-toggle]');
  if (cartToggle) {
    const drawer = document.getElementById('CartDrawer');
    if (drawer && drawer.open && STR.drawer !== false) { e.preventDefault(); drawer.open(cartToggle); }
  }
});

/* ------------------------------------------------------------------ cart drawer */
class CartDrawer extends ModalDrawer {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) { e.preventDefault(); this.applyDiscount(); }
    });
    this.updateShipping();
  }
  renderFrom(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-drawer');
    if (!fresh) return;
    const panel = this.querySelector('.drawer__panel');
    const freshPanel = fresh.querySelector('.drawer__panel');
    if (panel && freshPanel) panel.innerHTML = freshPanel.innerHTML;
    this.dataset.threshold = fresh.dataset.threshold;
    this.updateShipping();
  }
  updateShipping() {
    const bar = this.querySelector('[data-ship-bar]');
    if (!bar) return;
    const rate = (window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate)) || 1;
    const threshold = Math.round(parseInt(this.dataset.threshold || '0', 10) * rate);
    if (!threshold) return;
    const total = parseInt(bar.dataset.total || '0', 10);
    const pct = Math.min(100, Math.round((total / threshold) * 100));
    const text = bar.querySelector('[data-ship-text]');
    if (text) text.textContent = total >= threshold ? STR.freeShippingUnlocked : fill(STR.freeShippingRemaining, { __AMOUNT__: money(threshold - total) });
    const track = bar.querySelector('[role="progressbar"]');
    if (track) { track.setAttribute('aria-valuenow', pct); const fillEl = track.querySelector('span'); if (fillEl) fillEl.style.width = pct + '%'; }
  }
  setError(msg) {
    const el = this.querySelector('[data-cart-error]');
    if (el) el.textContent = msg || '';
  }
  async run(fn) {
    const panel = this.querySelector('.drawer__panel');
    panel && panel.classList.add('is-loading');
    try { const data = await fn(); await Cart.afterChange(data); }
    catch (err) { this.setError(err.message); }
    finally { const p = this.querySelector('.drawer__panel'); p && p.classList.remove('is-loading'); }
  }
  onClick(e) {
    const item = e.target.closest('[data-key]');
    const step = e.target.closest('[data-qty-change]');
    if (step && item) {
      const input = item.querySelector('[data-qty-input]');
      const next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(step.dataset.qtyChange, 10));
      this.run(() => Cart.change(item.dataset.key, next));
      return;
    }
    if (e.target.closest('[data-remove]') && item) { this.run(() => Cart.change(item.dataset.key, 0)); return; }
    const upsell = e.target.closest('[data-upsell]');
    if (upsell) { this.run(() => Cart.add([{ id: parseInt(upsell.dataset.upsell, 10), quantity: 1 }])); return; }
    if (e.target.closest('[data-discount-apply]')) { this.applyDiscount(); return; }
    if (e.target.closest('[data-discount-remove]')) { this.run(() => Cart.update({ discount: '' })); }
  }
  onChange(e) {
    if (e.target.matches('[data-qty-input]')) {
      const item = e.target.closest('[data-key]');
      const qty = Math.max(0, parseInt(e.target.value || '0', 10));
      this.run(() => Cart.change(item.dataset.key, qty));
    } else if (e.target.matches('[data-cart-note]')) {
      Cart.update({ note: e.target.value }).catch((err) => this.setError(err.message));
    }
  }
  async applyDiscount() {
    const input = this.querySelector('[data-discount-input]');
    const code = input && input.value.trim();
    if (!code) return;
    const panel = this.querySelector('.drawer__panel');
    panel && panel.classList.add('is-loading');
    try {
      const data = await Cart.update({ discount: code });
      await Cart.afterChange(data);
      const codes = data.discount_codes || [];
      const hit = codes.find((c) => c.code.toLowerCase() === code.toLowerCase());
      const msg = this.querySelector('[data-discount-msg]');
      if (msg) msg.textContent = hit && hit.applicable ? STR.discountApplied : STR.discountInvalid;
      const details = this.querySelector('[data-discount-input]');
      if (details) details.closest('details').open = true;
    } catch (err) { this.setError(err.message); }
    finally { const p = this.querySelector('.drawer__panel'); p && p.classList.remove('is-loading'); }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ------------------------------------------------------------------ announcement bar */
customElements.define('announcement-bar', class extends HTMLElement {
  connectedCallback() {
    if (this.dataset.rotate !== 'true' || reduceMotion) return;
    this.msgs = [...this.querySelectorAll('.ab__msg')];
    if (this.msgs.length < 2) return;
    this.i = 0;
    const ms = Math.max(3, parseInt(this.dataset.speed || '5', 10)) * 1000;
    const tick = () => {
      if (this.paused) return;
      this.msgs[this.i].classList.remove('is-active');
      this.i = (this.i + 1) % this.msgs.length;
      this.msgs[this.i].classList.add('is-active');
    };
    this.timer = setInterval(tick, ms);
    ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => { this.paused = true; }));
    ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => { this.paused = false; }));
  }
  disconnectedCallback() { clearInterval(this.timer); }
});

/* ------------------------------------------------------------------ sticky header */
customElements.define('sticky-header', class extends HTMLElement {
  connectedCallback() {
    const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
});

/* ------------------------------------------------------------------ marquee */
customElements.define('marquee-strip', class extends HTMLElement {
  connectedCallback() {
    this.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); this.classList.toggle('is-paused'); }
    });
  }
});

/* ------------------------------------------------------------------ media: uploaded video */
const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    const video = entry.target.querySelector('video');
    if (!video) return;
    if (entry.isIntersecting) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
    else video.pause();
  });
}, { threshold: 0.25 }) : null;

customElements.define('media-video', class extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    if (this.dataset.autoplay === 'true' && !reduceMotion && videoObserver) {
      video.muted = true;
      videoObserver.observe(this);
    } else if (!video.hasAttribute('controls')) {
      video.addEventListener('click', () => { if (video.paused) video.play(); else video.pause(); });
      video.setAttribute('tabindex', '0');
      video.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); if (video.paused) video.play(); else video.pause(); }
      });
    }
  }
  disconnectedCallback() { if (videoObserver) videoObserver.unobserve(this); }
});

/* ------------------------------------------------------------------ media: external video facade */
customElements.define('video-facade', class extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('.ms__play');
    if (btn) btn.addEventListener('click', () => this.load());
  }
  load() {
    const id = encodeURIComponent(this.dataset.id || '');
    const mute = this.dataset.muted === 'true' ? 1 : 0;
    const src = this.dataset.type === 'vimeo'
      ? `https://player.vimeo.com/video/${id}?autoplay=1&muted=${mute}&playsinline=1`
      : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=${mute}&playsinline=1&rel=0`;
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = this.dataset.title || '';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    this.replaceChildren(iframe);
    iframe.focus();
  }
});

/* ------------------------------------------------------------------ carousel */
class CarouselSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = () => [...this.track.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', () => this.go(this.index() - 1));
    if (next) next.addEventListener('click', () => this.go(this.index() + 1));
    this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.dot, 10))));
    let t;
    this.track.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => this.sync(), 60); }, { passive: true });
    this.track.addEventListener('keydown', (e) => {
      if (e.target !== this.track) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index() - 1); }
    });
    if (this.track.hasAttribute('data-drag')) this.enableDrag();
    this.sync();
  }
  index() {
    const slides = this.slides();
    const left = this.track.getBoundingClientRect().left;
    let best = 0; let dist = Infinity;
    slides.forEach((s, i) => { const d = Math.abs(s.getBoundingClientRect().left - left - parseFloat(getComputedStyle(this.track).paddingLeft || 0)); if (d < dist) { dist = d; best = i; } });
    return best;
  }
  go(i) {
    const slides = this.slides();
    if (!slides.length) return;
    const idx = Math.max(0, Math.min(slides.length - 1, i));
    const target = slides[idx];
    const pad = parseFloat(getComputedStyle(this.track).paddingLeft || 0);
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft - pad + (getComputedStyle(target).scrollSnapAlign.includes('center') ? (target.offsetWidth - this.track.clientWidth) / 2 + pad : 0), behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  sync() {
    const i = this.index();
    this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
  }
  enableDrag() {
    let down = false; let startX = 0; let startLeft = 0; let moved = false;
    this.track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) { moved = true; this.track.classList.add('is-dragging'); }
      this.track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      this.track.classList.remove('is-dragging');
      if (moved) this.go(this.index());
    });
    this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
  }
}
customElements.define('carousel-slider', CarouselSlider);

/* ------------------------------------------------------------------ lightbox (native <dialog>) */
const Lightbox = {
  open(items, index) {
    if (!items.length) return;
    this.items = items; this.i = index || 0;
    if (!this.dialog) this.build();
    this.show();
    if (!this.dialog.open) this.dialog.showModal();
  },
  build() {
    const d = document.createElement('dialog');
    d.className = 'lb';
    d.setAttribute('aria-label', STR.lightboxLabel || '');
    d.innerHTML = `<figure class="lb__fig"><img class="lb__img" alt=""><figcaption class="lb__cap"></figcaption></figure>
      <button type="button" class="icon-btn lb__btn lb__close" aria-label="${STR.close || ''}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
      <button type="button" class="icon-btn lb__btn lb__prev" aria-label="${STR.previous || ''}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg></button>
      <button type="button" class="icon-btn lb__btn lb__next" aria-label="${STR.next || ''}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></button>`;
    (document.getElementById('LightboxRoot') || document.body).appendChild(d);
    d.querySelector('.lb__close').addEventListener('click', () => d.close());
    d.querySelector('.lb__prev').addEventListener('click', () => { this.i = (this.i - 1 + this.items.length) % this.items.length; this.show(); });
    d.querySelector('.lb__next').addEventListener('click', () => { this.i = (this.i + 1) % this.items.length; this.show(); });
    d.addEventListener('click', (e) => { if (e.target === d) d.close(); });
    d.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') d.querySelector('.lb__next').click();
      if (e.key === 'ArrowLeft') d.querySelector('.lb__prev').click();
    });
    let x0 = null;
    d.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    d.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) > 40) d.querySelector(dx < 0 ? '.lb__next' : '.lb__prev').click();
    });
    this.dialog = d;
  },
  show() {
    const it = this.items[this.i];
    const img = this.dialog.querySelector('.lb__img');
    img.src = it.src; img.alt = it.alt || '';
    this.dialog.querySelector('.lb__cap').textContent = fill(STR.imageOf, { __I__: this.i + 1, __N__: this.items.length });
    const multi = this.items.length > 1;
    this.dialog.querySelector('.lb__prev').hidden = !multi;
    this.dialog.querySelector('.lb__next').hidden = !multi;
  },
};
document.addEventListener('click', (e) => {
  const zoom = e.target.closest('[data-zoom]');
  if (!zoom) return;
  const holder = zoom.closest('[data-full]');
  const scope = zoom.closest('[data-lightbox], .shopify-section') || document;
  const all = [...scope.querySelectorAll('[data-full]')].filter((el) => !el.hidden);
  const items = all.map((el) => ({ src: el.dataset.full, alt: el.dataset.alt }));
  Lightbox.open(items, Math.max(0, all.indexOf(holder)));
});

/* ------------------------------------------------------------------ product buy box */
class ProductBuybox extends HTMLElement {
  connectedCallback() {
    try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
    this.form = this.querySelector('form');
    this.idInput = this.querySelector('[data-variant-input]');
    this.colourIndex = parseInt(this.dataset.colourIndex || '-1', 10);
    this.current = this.variants.find((v) => String(v.id) === this.idInput.value) || this.variants[0];
    this.unitColours = [];
    this.setupGallery();
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-input]')) this.onOptionChange();
      if (e.target.matches('[name^="tier-"]')) this.refresh();
      if (e.target.matches('[data-qty]')) this.refresh();
    });
    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-step]');
      if (step) {
        const input = this.querySelector('[data-qty]');
        input.value = Math.max(1, Math.min(20, parseInt(input.value || '1', 10) + parseInt(step.dataset.step, 10)));
        this.refresh();
      }
      const sw = e.target.closest('.bb__unit-sw');
      if (sw) this.pickUnitColour(sw);
    });
    this.addEventListener('keydown', (e) => {
      const sw = e.target.closest('.bb__unit-sw');
      if (!sw || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
      e.preventDefault();
      const sibs = [...sw.parentElement.children];
      const n = sibs[(sibs.indexOf(sw) + (e.key === 'ArrowRight' ? 1 : sibs.length - 1)) % sibs.length];
      n.focus(); this.pickUnitColour(n);
    });
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
    this.markAvailability();
    this.refresh();
    this.watchAtc();
  }

  /* ---------- gallery ---------- */
  setupGallery() {
    this.slides = this.querySelector('[data-slides]');
    if (!this.slides) return;
    this.thumbWrap = this.querySelector('.bb__thumbs');
    this.slideOrder = [...this.slides.children];
    this.thumbOrder = this.thumbWrap ? [...this.thumbWrap.children] : [];
    this.counter = this.querySelector('[data-counter]');
    const prev = this.querySelector('[data-slide-prev]');
    const next = this.querySelector('[data-slide-next]');
    if (prev) prev.addEventListener('click', () => { const n = this.visibleSlides().length; this.goSlide((this.slideIndex() - 1 + n) % n); });
    if (next) next.addEventListener('click', () => { const n = this.visibleSlides().length; this.goSlide((this.slideIndex() + 1) % n); });
    this.thumbOrder.forEach((t) => t.addEventListener('click', () => this.goSlide(parseInt(t.dataset.thumb, 10))));
    let tm;
    this.slides.addEventListener('scroll', () => { clearTimeout(tm); tm = setTimeout(() => this.syncSlide(), 60); }, { passive: true });
    this.slides.addEventListener('keydown', (e) => {
      if (e.target !== this.slides) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); next && next.click(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev && prev.click(); }
    });
    this.filterGallery();
  }
  visibleSlides() { return this.slides ? [...this.slides.children].filter((s) => !s.hidden) : []; }
  selectedColour() {
    return this.current && this.colourIndex > -1 ? String(this.current.options[this.colourIndex]).toLowerCase() : '';
  }
  // Colours whose photos the gallery shows: the main colour, or every colour picked for the bands in a bundle
  // (the one picked last comes first).
  galleryColours() {
    const main = this.selectedColour();
    const wrap = this.querySelector('[data-units-wrap]');
    const per = wrap && !wrap.hidden ? this.unitColours.map((c) => String(c).toLowerCase()) : [];
    const list = [...new Set(per.length ? per : (main ? [main] : []))];
    const f = this.focusColour;
    return f && list.includes(f) ? [f, ...list.filter((c) => c !== f)] : list;
  }
  // Shows the chosen colours' own photos first, then photos that aren't tied to a colour; hides other colours' photos.
  filterGallery() {
    if (!this.slides) return;
    const cs = this.galleryColours();
    this.galleryKey = cs.join('|');
    const pairs = this.slideOrder.map((s, i) => ({ s, t: this.thumbOrder[i], tag: (s.dataset.colour || '').toLowerCase() }));
    const ordered = cs.length ? [...cs.flatMap((c) => pairs.filter((x) => x.tag === c)), ...pairs.filter((x) => !x.tag), ...pairs.filter((x) => x.tag && !cs.includes(x.tag))] : pairs;
    let n = 0;
    ordered.forEach((x) => {
      const hide = !!(cs.length && x.tag && !cs.includes(x.tag));
      x.s.hidden = hide;
      this.slides.appendChild(x.s);
      if (x.t) { x.t.hidden = hide; x.t.dataset.thumb = hide ? '-1' : String(n); this.thumbWrap.appendChild(x.t); }
      if (!hide) n += 1;
    });
    this.thumbs = this.thumbOrder.filter((t) => !t.hidden).sort((a, b) => parseInt(a.dataset.thumb, 10) - parseInt(b.dataset.thumb, 10));
    const many = n > 1;
    this.querySelectorAll('[data-slide-prev],[data-slide-next],[data-counter]').forEach((el) => { el.hidden = !many; });
    if (this.thumbWrap) this.thumbWrap.hidden = !many;
    this.goSlide(0, true);
  }
  slideIndex() { return this.slides ? Math.round(this.slides.scrollLeft / Math.max(1, this.slides.clientWidth)) : 0; }
  goSlide(i, instant) {
    if (!this.slides || i < 0) return;
    this.slides.scrollTo({ left: i * this.slides.clientWidth, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    this.syncSlide(i);
  }
  syncSlide(forced) {
    const vis = this.visibleSlides();
    const i = Math.min(typeof forced === 'number' ? forced : this.slideIndex(), Math.max(0, vis.length - 1));
    (this.thumbs || []).forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
    if (this.counter) this.counter.textContent = `${i + 1} / ${vis.length}`;
    const badge = this.querySelector('[data-colour-badge]');
    if (badge) {
      const tag = vis[i] ? (vis[i].dataset.colour || '').toLowerCase() : '';
      const show = !!tag && this.galleryColours().includes(tag);
      badge.hidden = !show;
      if (show) this.paintBadge(badge, tag);
    }
  }
  // Badge shows the colour of the photo on screen, with that colour's swatch dot.
  paintBadge(badge, tag) {
    const v = this.variants.find((x) => String(x.options[this.colourIndex]).toLowerCase() === tag);
    const name = badge.querySelector('[data-colour-name]');
    if (name && v) name.textContent = v.options[this.colourIndex];
    const dot = badge.querySelector('[data-colour-dot]');
    const input = [...this.querySelectorAll(`[data-option-index="${this.colourIndex}"] [data-option-input]`)].find((el) => el.value.toLowerCase() === tag);
    const sw = input && input.nextElementSibling && input.nextElementSibling.querySelector('.sw__dot');
    if (dot && sw) dot.style.cssText = sw.getAttribute('style') || '';
  }
  showVariantMedia(v) {
    if (!v || !this.slides) return;
    const vis = this.visibleSlides();
    const idx = v.media ? vis.findIndex((s) => s.dataset.mediaId === String(v.media)) : -1;
    this.goSlide(idx > -1 ? idx : 0);
  }

  /* ---------- variants ---------- */
  selectedOptions() {
    return [...this.querySelectorAll('[data-option-index]')].map((fs) => {
      const c = fs.querySelector('[data-option-input]:checked');
      return c ? c.value : null;
    });
  }
  onOptionChange() {
    const opts = this.selectedOptions();
    const v = this.variants.find((x) => x.options.every((o, i) => opts[i] === null || o === opts[i]));
    if (!v) { this.current = null; this.refresh(); return; }
    this.current = v;
    this.idInput.value = v.id;
    this.querySelectorAll('[data-option-index]').forEach((fs, i) => {
      const cur = fs.querySelector('[data-option-current]');
      if (cur) cur.textContent = opts[i];
    });
    this.unitColours = [];
    this.focusColour = null;
    this.filterGallery();
    this.showVariantMedia(v);
    this.markAvailability();
    this.refresh();
    if (this.dataset.updateUrl === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: v, product: this.dataset.productUrl } }));
  }
  markAvailability() {
    const opts = this.selectedOptions();
    this.querySelectorAll('[data-option-index]').forEach((fs) => {
      const idx = parseInt(fs.dataset.optionIndex, 10);
      fs.querySelectorAll('[data-option-input]').forEach((input) => {
        const test = opts.slice(); test[idx] = input.value;
        const match = this.variants.find((v) => v.options.every((o, i) => o === test[i]));
        const state = input.nextElementSibling && input.nextElementSibling.querySelector('[data-state]');
        const soldOut = !match || !match.available;
        if (state) state.textContent = soldOut ? (STR.soldOutShort || '') : '';
      });
    });
  }
  variantFor(colour) {
    if (!this.current) return null;
    const opts = this.current.options.slice();
    opts[this.colourIndex] = colour;
    return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
  }

  /* ---------- tiers ---------- */
  tier() {
    const input = this.querySelector('[name^="tier-"]:checked');
    return input ? input.closest('[data-tier]') : null;
  }
  // Mirrors the store's automatic discounts: the best tier discount whose quantity the cart line reaches.
  discountFor(qty) {
    let pct = 0;
    this.querySelectorAll('[data-tier][data-target="quantity"]').forEach((t) => {
      const q = parseInt(t.dataset.qty, 10), d = parseInt(t.dataset.discount || '0', 10);
      if (qty >= q && d > pct) pct = d;
    });
    return pct;
  }
  bundles() {
    const q = this.querySelector('[data-qty]');
    return q ? Math.max(1, parseInt(q.value || '1', 10)) : 1;
  }
  plan() {
    const v = this.current;
    const t = this.tier();
    const bundles = this.bundles();
    if (!t || t.dataset.target === 'quantity') {
      const qty = (t ? parseInt(t.dataset.qty, 10) : 1) * bundles;
      const unit = v ? v.price : 0;
      const compare = v ? v.compare : 0;
      const pct = this.discountFor(qty);
      const full = unit * qty;
      const total = pct > 0 ? full - Math.round(full * pct / 100) : full;
      return { kind: 'quantity', qty, units: qty, total, compareTotal: Math.max(compare * qty, pct > 0 ? full : 0), available: !!(v && v.available), variant: v };
    }
    const qty = bundles;
    const units = parseInt(t.dataset.units || '1', 10) * bundles;
    const unit = parseInt(t.dataset.unitPrice || '0', 10);
    const compare = parseInt(t.dataset.unitCompare || '0', 10);
    const available = !t.querySelector('input').hasAttribute('data-unavailable') && !!t.dataset.variant;
    return { kind: 'fixed', id: parseInt(t.dataset.variant, 10), qty, units, total: unit * qty, compareTotal: compare * qty, available };
  }
  refresh() {
    const v = this.current;
    const price = this.querySelector('[data-price]');
    const compare = this.querySelector('[data-compare]');
    const save = this.querySelector('[data-save]');
    const unit = this.querySelector('[data-unit]');
    const sku = this.querySelector('[data-sku]');
    if (v) {
      if (price) price.textContent = v.priceF;
      if (compare) { compare.hidden = !(v.compare > v.price); const s = compare.lastChild; if (s && v.compareF) s.textContent = v.compareF; }
      if (save) { save.hidden = !(v.compare > v.price); save.textContent = v.compare > v.price ? fill(STR.save, { __AMOUNT__: money(v.compare - v.price) }) : ''; }
      if (unit) { unit.hidden = !v.unitF; unit.textContent = v.unitF || ''; }
      if (sku) { sku.hidden = !v.sku; const s = sku.querySelector('span'); if (s) s.textContent = v.sku || ''; }
    }
    // Tier rows reflect the selected variant's real price
    this.querySelectorAll('[data-tier]').forEach((t) => {
      if (t.dataset.target !== 'quantity' || !v) return;
      const q = parseInt(t.dataset.qty, 10);
      const units = parseInt(t.dataset.units || q, 10) || q;
      const pct = this.discountFor(q);
      const full = v.price * q;
      const total = pct > 0 ? full - Math.round(full * pct / 100) : full;
      const cmp = Math.max(v.compare * q, pct > 0 ? full : 0);
      const tt = t.querySelector('[data-tier-total]'); if (tt) tt.textContent = money(total);
      const tc = t.querySelector('[data-tier-compare]'); if (tc) { tc.hidden = !(cmp > total); tc.textContent = money(cmp); }
      const tp = t.querySelector('[data-tier-per]'); if (tp) tp.textContent = fill(STR.perUnit, { __PRICE__: money(Math.round(total / units)) });
    });
    const plan = this.plan();
    const sumTotal = this.querySelector('[data-sum-total]');
    if (sumTotal) {
      sumTotal.textContent = money(plan.total);
      this.querySelector('[data-sum-units]').textContent = plan.units;
      this.querySelector('[data-sum-per]').textContent = money(Math.round(plan.total / Math.max(1, plan.units)));
    }
    this.renderUnits(plan);
    if (this.slides && this.galleryColours().join('|') !== this.galleryKey) this.filterGallery();
    else if (this.slides) this.syncSlide();
    this.renderInventory(plan);
    const atc = this.querySelector('[data-atc]');
    const label = this.querySelector('[data-atc-text]');
    const ok = plan.available && (plan.kind === 'fixed' || !!v);
    atc.disabled = !ok;
    label.textContent = ok ? `${atc.dataset.label} — ${money(plan.total)}` : (v ? STR.soldOut : STR.unavailable);
    this.setError('');
    document.dispatchEvent(new CustomEvent('buybox:update', { detail: { label: label.textContent, ok, total: money(plan.total), variantTitle: v ? v.title : '', source: this } }));
  }
  renderInventory(plan) {
    const note = this.querySelector('[data-inventory-note]');
    if (!note) return;
    const v = this.current;
    const threshold = parseInt(this.dataset.inventoryThreshold || '0', 10);
    if (this.dataset.inventory === 'true' && plan.kind === 'quantity' && v && v.inv !== null && v.inv > 0 && v.inv <= threshold) {
      note.hidden = false; note.textContent = fill(STR.onlyLeft, { __COUNT__: v.inv });
    } else { note.hidden = true; note.textContent = ''; }
  }

  /* ---------- per-band colours ---------- */
  renderUnits(plan) {
    const wrap = this.querySelector('[data-units-wrap]');
    if (!wrap || this.colourIndex < 0 || this.dataset.perUnitColour !== 'true') return;
    const rows = wrap.querySelector('[data-unit-rows]');
    const tpl = wrap.querySelector('[data-unit-template]');
    const n = plan.kind === 'quantity' ? Math.min(plan.qty, 20) : 0;
    wrap.hidden = n < 2;
    if (n < 2 || !this.current) { rows.replaceChildren(); return; }
    const base = this.current.options[this.colourIndex];
    while (this.unitColours.length < n) this.unitColours.push(base);
    this.unitColours.length = n;
    const count = wrap.querySelector('[data-units-count]');
    if (count) count.textContent = fill(STR.unitsCount, { __N__: n });
    rows.replaceChildren();
    for (let i = 0; i < n; i += 1) {
      const row = tpl.content.firstElementChild.cloneNode(true);
      const label = fill(STR.unitLabel, { __N__: i + 1 });
      row.querySelector('[data-unit-label]').textContent = label;
      row.querySelector('[data-unit-value]').textContent = this.unitColours[i];
      row.setAttribute('aria-label', label);
      row.dataset.unit = i;
      row.querySelectorAll('.bb__unit-sw').forEach((sw) => {
        const on = sw.dataset.value === this.unitColours[i];
        const v = this.variantFor(sw.dataset.value);
        sw.setAttribute('aria-checked', on ? 'true' : 'false');
        sw.tabIndex = on ? 0 : -1;
        sw.disabled = !(v && v.available);
      });
      rows.appendChild(row);
    }
  }
  pickUnitColour(sw) {
    const row = sw.closest('[data-unit]');
    const i = parseInt(row.dataset.unit, 10);
    this.unitColours[i] = sw.dataset.value;
    row.querySelectorAll('.bb__unit-sw').forEach((b) => { const on = b === sw; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
    row.querySelector('[data-unit-value]').textContent = sw.dataset.value;
    this.focusColour = String(sw.dataset.value).toLowerCase();
    this.filterGallery();
  }

  /* ---------- add to cart ---------- */
  items() {
    const plan = this.plan();
    if (plan.kind === 'fixed') return [{ id: plan.id, quantity: plan.qty }];
    const wrap = this.querySelector('[data-units-wrap]');
    if (wrap && !wrap.hidden && this.unitColours.length > 1) {
      const counts = {};
      this.unitColours.forEach((c) => { counts[c] = (counts[c] || 0) + 1; });
      const out = [];
      for (const [colour, quantity] of Object.entries(counts)) {
        const v = this.variantFor(colour);
        if (!v || !v.available) throw new Error(STR.soldOut);
        out.push({ id: v.id, quantity });
      }
      return out;
    }
    return [{ id: this.current.id, quantity: plan.qty }];
  }
  setError(msg) { const el = this.querySelector('[data-error]'); if (el) el.textContent = msg || ''; }
  async submit() {
    const atc = this.querySelector('[data-atc]');
    const label = this.querySelector('[data-atc-text]');
    if (atc.disabled) return;
    let message = '';
    atc.disabled = true; atc.setAttribute('aria-busy', 'true'); label.textContent = STR.adding;
    try {
      const data = await Cart.add(this.items());
      await Cart.afterChange(data, { open: true });
    } catch (err) {
      message = err.message || STR.errorGeneric;
    } finally {
      atc.removeAttribute('aria-busy');
      this.refresh();
      this.setError(message);
    }
  }
  watchAtc() {
    const atc = this.querySelector('[data-atc]');
    if (!atc || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      document.dispatchEvent(new CustomEvent('buybox:visibility', { detail: { past, source: this } }));
    }).observe(atc);
  }
}
customElements.define('product-buybox', ProductBuybox);

/* ------------------------------------------------------------------ sticky add to cart */
customElements.define('sticky-atc', class extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-satc-add]');
    document.addEventListener('buybox:visibility', (e) => this.toggle(e.detail.past));
    document.addEventListener('buybox:update', (e) => {
      this.source = e.detail.source;
      const price = this.querySelector('[data-satc-price]');
      const vt = this.querySelector('[data-satc-variant]');
      if (price) price.textContent = e.detail.total;
      if (vt && vt.textContent && e.detail.variantTitle) vt.textContent = `${e.detail.variantTitle} · `;
      this.btn.disabled = !e.detail.ok;
    });
    this.btn.addEventListener('click', () => this.add());
    if (!document.querySelector('product-buybox')) {
      window.addEventListener('scroll', () => this.toggle(window.scrollY > window.innerHeight * 0.8), { passive: true });
    }
  }
  toggle(on) {
    this.classList.toggle('is-visible', on);
    this.setAttribute('aria-hidden', on ? 'false' : 'true');
    this.btn.tabIndex = on ? 0 : -1;
  }
  async add() {
    if (this.source) { this.source.submit(); return; }
    this.btn.disabled = true;
    try {
      const data = await Cart.add([{ id: parseInt(this.dataset.variantId, 10), quantity: 1 }]);
      await Cart.afterChange(data, { open: true });
    } catch (err) {
      this.btn.setAttribute('title', err.message);
    } finally { this.btn.disabled = false; }
  }
});

/* ------------------------------------------------------------------ review wall */
customElements.define('review-wall', class extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-review]')];
    if (!this.cards.length) return;
    this.step = parseInt(this.dataset.step || '8', 10);
    this.limit = parseInt(this.dataset.initial || '8', 10);
    this.filter = 'all';
    this.querySelectorAll('[data-filter]').forEach((b) => b.addEventListener('click', () => {
      this.filter = b.dataset.filter;
      this.limit = parseInt(this.dataset.initial || '8', 10);
      this.querySelectorAll('[data-filter]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
      this.apply();
    }));
    const more = this.querySelector('[data-more]');
    if (more) more.addEventListener('click', () => { this.limit += this.step; this.apply(); });
    this.apply();
  }
  apply() {
    const match = this.cards.filter((c) => this.filter === 'all'
      || (this.filter === 'photo' && c.dataset.photo === 'true')
      || (this.filter === '5' && c.dataset.rating === '5'));
    this.cards.forEach((c) => { c.hidden = true; });
    match.slice(0, this.limit).forEach((c) => { c.hidden = false; });
    const more = this.querySelector('[data-more]');
    if (more) more.hidden = match.length <= this.limit;
    const empty = this.querySelector('[data-empty]');
    if (empty) empty.hidden = match.length > 0;
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = fill(STR.reviewsShown, { __SHOWN__: Math.min(this.limit, match.length), __TOTAL__: match.length });
  }
});

/* ------------------------------------------------------------------ theme editor support */
document.addEventListener('shopify:section:load', () => Cart.refreshCount());
document.addEventListener('shopify:block:select', (e) => {
  const slide = e.target.closest('[data-slide]');
  const track = slide && slide.closest('[data-track]');
  if (track) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'auto' });
  const details = e.target.closest('details');
  if (details) details.open = true;
});
